import { useToast } from "@chakra-ui/react";
import { Bundle, Resource } from "@medplum/fhirtypes";
import { useState } from "react";
import { getSearchResults } from "../../../api/search";
import { capture } from "../../../shared/capture";
import { usePatientConsolidatedContext } from "../shared-logic/consolidated-context";
import { PatientConsolidatedStateActionType } from "./consolidated-context/reducer";
import { errorToString } from "@metriport/shared";

export type UseSearch = {
  searchResults: Bundle<Resource> | undefined;
  onSearch: (question: string) => Promise<void>;
  isSearchingResults: boolean;
};

export function useSearch({ patientId }: { patientId: string }): UseSearch {
  const { patientConsolidatedDispatch } = usePatientConsolidatedContext();
  const toast = useToast();
  const [isSearchingResults, setIsSearchingResults] = useState(false);
  const [searchResults, setSearchResults] = useState<Bundle<Resource> | undefined>(undefined);

  const onSearch = async (question: string) => {
    try {
      setIsSearchingResults(true);

      const search = await getSearchResults(patientId, question);

      if (search) {
        patientConsolidatedDispatch({
          type: PatientConsolidatedStateActionType.setPatientsSearchResults,
          patientId,
          searchResultsBundle: search,
        });
      }
      setSearchResults(search);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to search",
        status: "error",
      });
      const errorMessage = errorToString(error);
      capture.error("Failed to search", {
        extra: { patientId, context: `patient.search`, errorMessage, error },
      });
    } finally {
      setIsSearchingResults(false);
    }
  };

  return {
    searchResults,
    onSearch,
    isSearchingResults,
  };
}
